import type { HRCase, CaseDriver } from '../types/case.types'

// ── Helpers ────────────────────────────────────────────────────────────────
const _base = () => {
  const d = new Date()
  d.setHours(9, 0, 0, 0)
  return d
}
const ts = (offsetMinutes: number) => {
  const d = _base()
  d.setMinutes(d.getMinutes() + offsetMinutes)
  return d.toISOString()
}
const sla = (hoursFromNow: number) => {
  const d = _base()
  d.setHours(d.getHours() + hoursFromNow)
  return d.toISOString()
}

// ── 12 Mock HRCases ────────────────────────────────────────────────────────

export const mockCases: HRCase[] = [
  // ── CASE 1 — L1 / Resolved / Autonomous ──────────────────────────────
  {
    id: 'case-001',
    caseRef: 'CASE-2026-0481',
    tier: 'L1',
    type: 'pto_leave_balance',
    status: 'resolved',
    channel: 'teams',
    submittedAt: ts(-210),
    resolvedAt: ts(-207),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-071',
      name: 'Sarah Mitchell',
      email: 'sarah.mitchell@company.com',
      department: 'Engineering',
      location: 'Austin, TX',
      employeeId: 'EMP-0071',
      manager: 'James Caldwell',
      startDate: '2023-03-14',
    },
    subject: 'How many PTO days do I have left for the year?',
    description:
      'Employee asked via Teams: "Hey, quick question. Can someone tell me how many PTO days I have remaining? I want to plan a trip in August and need to know if I can take 10 consecutive days."',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-PTO-001',
        policyName: 'PTO Balance Inquiry: Standard',
        triggered: true,
        outcome: 'pass',
        detail:
          'Employee has 14.5 days remaining. Request is informational only. No approval required.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified case as PTO balance inquiry (L1)',
        timestamp: ts(-210),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Confidence: 0.97. Routed to auto-resolution pathway.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Queried leave balance for EMP-0071',
        timestamp: ts(-209),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Accrued: 20 days. Used: 5.5 days. Remaining: 14.5 days.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Sent balance confirmation to employee via Teams and closed ticket',
        timestamp: ts(-207),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail:
          'Response: "Hi Sarah, you currently have 14.5 PTO days remaining for 2026. 10 consecutive days is within your balance. Let me know if you need anything else!"',
      },
    ],
    resolutionSummary:
      'Queried Workday leave module, confirmed 14.5 days remaining, responded to employee within 3 minutes.',
    chatHistory: [
      { id: 'c1-m1', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Hey, quick question — how many PTO days do I have left this year? I want to plan a trip in August and need to know if I can take 10 consecutive days.', timestamp: ts(-210) },
      { id: 'c1-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Sarah! Let me pull your Workday leave balance right now — one moment.', timestamp: ts(-210) },
      { id: 'c1-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Got it. As of today you have **14.5 PTO days** remaining for 2026. You\'ve accrued 20 days and used 5.5 so far.', timestamp: ts(-209) },
      { id: 'c1-m4', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Great! So 10 consecutive days in August should be fine?', timestamp: ts(-209) },
      { id: 'c1-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes — 10 days is comfortably within your 14.5-day balance. You\'d still have 4.5 days left after your trip.', timestamp: ts(-208) },
      { id: 'c1-m6', sender: 'employee', senderName: 'Sarah Mitchell', message: 'What about rollover? If I don\'t use the remaining 4.5 days by December, do they carry over?', timestamp: ts(-208) },
      { id: 'c1-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes — the company policy allows up to 5 days to roll over into 2027. So any unused days up to that cap will carry over automatically. You\'re well within the rollover limit.', timestamp: ts(-207) },
      { id: 'c1-m8', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Perfect. How do I submit the leave request for August?', timestamp: ts(-207) },
      { id: 'c1-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'You can submit it directly in Workday: go to **Time Off → Request → Vacation**, select your dates, and submit. Your manager James Caldwell will get an auto-notification for approval.', timestamp: ts(-208) },
      { id: 'c1-m10', sender: 'employee', senderName: 'Sarah Mitchell', message: 'How far in advance should I submit?', timestamp: ts(-207) },
      { id: 'c1-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Policy recommends submitting at least 2 weeks before the leave start date for requests over 5 days. For 10 consecutive days, the sooner the better — especially for August peak season.', timestamp: ts(-207) },
      { id: 'c1-m12', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Got it. Thanks for the quick help — I\'ll submit it today!', timestamp: ts(-207) },
    ],
    relatedSystems: ['Workday', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 2 — L1 / Resolved / Autonomous ──────────────────────────────
  {
    id: 'case-002',
    caseRef: 'CASE-2026-0482',
    tier: 'L1',
    type: 'benefits_policy_question',
    status: 'resolved',
    channel: 'email',
    submittedAt: ts(-185),
    resolvedAt: ts(-180),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-034',
      name: 'David Park',
      email: 'david.park@company.com',
      department: 'Finance',
      location: 'New York, NY',
      employeeId: 'EMP-0034',
      manager: 'Linda Nguyen',
      startDate: '2021-08-02',
    },
    subject: 'HSA contribution limit for 2026: can I catch up?',
    description:
      'David emailed HR asking about the 2026 HSA annual contribution limit and whether he qualifies for the catch-up contribution given he turns 55 in October.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-BEN-003',
        policyName: 'Benefits FAQ: HSA Limits',
        triggered: true,
        outcome: 'pass',
        detail:
          'IRS 2026 HSA limit: $4,300 (individual) / $8,550 (family). Catch-up: +$1,000 for age 55+. Employee qualifies.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as Benefits Policy Question (L1)',
        timestamp: ts(-185),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Email parsed. No PII exposure. Routed to knowledge base lookup.',
      },
      {
        agentName: 'Knowledge Base Agent',
        action: 'Retrieved HSA limit policy from SharePoint Knowledge Base',
        timestamp: ts(-183),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Doc: 2026 Benefits Guide v3.1. Confidence: 0.99.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Verified catch-up eligibility from Workday profile',
        timestamp: ts(-182),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'DOB confirmed. Turns 55 in Oct 2026. Catch-up contribution eligible.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Sent personalized benefits response via email and closed ticket',
        timestamp: ts(-180),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail:
          'Confirmed $4,300 + $1,000 catch-up = $5,300 total. Included enrollment link.',
      },
    ],
    resolutionSummary:
      'Retrieved 2026 HSA limits from knowledge base, verified employee catch-up eligibility via DOB in Workday, responded within 5 minutes.',
    chatHistory: [
      { id: 'c2-m1', sender: 'employee', senderName: 'David Park', message: 'Hi, I have a question about HSA contribution limits for 2026. I turn 55 in October — am I eligible for the catch-up contribution?', timestamp: ts(-185) },
      { id: 'c2-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi David! Great question. Let me pull the current IRS limits and verify your eligibility from your Workday profile.', timestamp: ts(-184) },
      { id: 'c2-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Confirmed: 2026 HSA limits are **$4,300** for individual coverage and **$8,550** for family. Since you turn 55 this calendar year, you qualify for a **$1,000 catch-up contribution**.', timestamp: ts(-182) },
      { id: 'c2-m4', sender: 'employee', senderName: 'David Park', message: 'So my total individual limit is $5,300?', timestamp: ts(-181) },
      { id: 'c2-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Exactly — $4,300 standard + $1,000 catch-up = **$5,300 total**. This applies to the full calendar year, so if you haven\'t maxed out yet, you can still make additional contributions before December 31.', timestamp: ts(-181) },
      { id: 'c2-m6', sender: 'employee', senderName: 'David Park', message: 'What\'s the difference between an HSA and FSA? I want to make sure I\'m using the right one.', timestamp: ts(-180) },
      { id: 'c2-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Key differences: An **HSA** rolls over year to year and is yours even if you change jobs — but requires a High Deductible Health Plan (HDHP). An **FSA** doesn\'t require HDHP but is use-it-or-lose-it (up to a $610 rollover in 2026). You\'re enrolled in the HDHP, so the HSA is the right choice for you.', timestamp: ts(-179) },
      { id: 'c2-m8', sender: 'employee', senderName: 'David Park', message: 'That makes sense. When is the next enrollment window to change my contribution amount?', timestamp: ts(-178) },
      { id: 'c2-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Open Enrollment runs November 1–15. However, you can also change your HSA contribution mid-year if you have a qualifying life event (e.g., marriage, birth of a child, or a change in health plan).', timestamp: ts(-178) },
      { id: 'c2-m10', sender: 'employee', senderName: 'David Park', message: 'Great. Can you send me the link to update my election?', timestamp: ts(-177) },
      { id: 'c2-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Of course — here\'s your direct link: **Benefits Portal → Health Savings → Update HSA Election**. You\'re all set, David! Case closed.', timestamp: ts(-180) },
    ],
    relatedSystems: ['Workday', 'SharePoint Knowledge Base', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 3 — L1 / Resolved / Autonomous ──────────────────────────────
  {
    id: 'case-003',
    caseRef: 'CASE-2026-0483',
    tier: 'L1',
    type: 'address_data_change',
    status: 'resolved',
    channel: 'portal',
    submittedAt: ts(-160),
    resolvedAt: ts(-158),
    slaDeadline: sla(6),
    slaBreached: false,
    employee: {
      id: 'emp-112',
      name: 'Jennifer Lopez',
      email: 'jennifer.lopez@company.com',
      department: 'Marketing',
      location: 'Chicago, IL',
      employeeId: 'EMP-0112',
      manager: 'Karen Schmidt',
      startDate: '2022-11-07',
    },
    subject: 'Update my home address (moved last weekend)',
    description:
      'Employee submitted address update via HR portal. New address: 2847 N. Halsted St, Apt 4B, Chicago, IL 60657. Previous address was 914 W. Diversey Pkwy, Chicago, IL 60614.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-DATA-002',
        policyName: 'Self-Service Data Change: Address',
        triggered: true,
        outcome: 'pass',
        detail:
          'Domestic address change. No state tax nexus change. Qualifies for auto-processing.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as address data change (L1, auto-processable)',
        timestamp: ts(-160),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Same state. No payroll tax jurisdiction change. Safe for autonomous update.',
      },
      {
        agentName: 'Transaction Execution Agent',
        action: 'Updated home address in Workday HCM',
        timestamp: ts(-159),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Address updated. Effective date: today. Change logged in audit trail.',
      },
      {
        agentName: 'Payroll Context Agent',
        action: 'Verified no payroll jurisdiction change required',
        timestamp: ts(-158),
        systemTouched: 'Payroll System',
        outcome: 'success',
        detail: 'IL→IL move. Withholding unchanged. No payroll action needed.',
      },
    ],
    resolutionSummary:
      'Address updated in Workday within 2 minutes. Payroll verified no jurisdiction change. Employee confirmation sent.',
    chatHistory: [
      { id: 'c3-m1', sender: 'employee', senderName: 'Jennifer Lopez', message: 'Hi! I just moved to a new apartment and need to update my home address in the system. I moved last weekend.', timestamp: ts(-160) },
      { id: 'c3-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Jennifer! No problem at all. What is your new home address?', timestamp: ts(-160) },
      { id: 'c3-m3', sender: 'employee', senderName: 'Jennifer Lopez', message: '2847 N. Halsted St, Apt 4B, Chicago, IL 60657', timestamp: ts(-159) },
      { id: 'c3-m4', sender: 'ai_agent', senderName: 'AI Agent', message: 'Got it! This is an IL to IL move, so no state tax jurisdiction change is needed. I can process this automatically right now.', timestamp: ts(-159) },
      { id: 'c3-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Done — I\'ve updated your address in Workday to **2847 N. Halsted St, Apt 4B, Chicago, IL 60657** effective today. The change is logged in your profile audit trail.', timestamp: ts(-158) },
      { id: 'c3-m6', sender: 'employee', senderName: 'Jennifer Lopez', message: 'That was fast! Will this update automatically for my W-2 at the end of the year?', timestamp: ts(-158) },
      { id: 'c3-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes — your Workday address is the source of record for year-end W-2 mailing. Your W-2 for 2026 will be sent to the new address in January 2027. No additional action needed on your end.', timestamp: ts(-157) },
      { id: 'c3-m8', sender: 'employee', senderName: 'Jennifer Lopez', message: 'Should I also update my emergency contact address?', timestamp: ts(-157) },
      { id: 'c3-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Emergency contacts are separate in Workday. You can update them under **Personal Information → Emergency Contacts**. Would you like me to walk you through it?', timestamp: ts(-157) },
      { id: 'c3-m10', sender: 'employee', senderName: 'Jennifer Lopez', message: 'I\'ll do it myself, thanks! Really appreciate how quick this was.', timestamp: ts(-158) },
    ],
    relatedSystems: ['Workday', 'Payroll System', 'ServiceNow'],
  },

  // ── CASE 4 — L1 / Resolved / Autonomous ──────────────────────────────
  {
    id: 'case-004',
    caseRef: 'CASE-2026-0484',
    tier: 'L1',
    type: 'employment_verification',
    status: 'resolved',
    channel: 'email',
    submittedAt: ts(-140),
    resolvedAt: ts(-136),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-058',
      name: 'Alex Thompson',
      email: 'alex.thompson@company.com',
      department: 'Sales',
      location: 'Denver, CO',
      employeeId: 'EMP-0058',
      manager: 'Brian Hayes',
      startDate: '2020-01-13',
    },
    subject: 'Need employment verification letter for mortgage application',
    description:
      'Alex is applying for a mortgage and the lender requires an official employment verification letter confirming title, start date, employment type, and annual salary.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-VER-001',
        policyName: 'Employment Verification: Standard Letter',
        triggered: true,
        outcome: 'pass',
        detail:
          'Standard verification request. Employee active. Salary disclosure authorized. Auto-generate permitted.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as employment verification (L1)',
        timestamp: ts(-140),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Standard request. Employee consent implicitly given per HR policy.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Retrieved employment data from Workday',
        timestamp: ts(-138),
        systemTouched: 'Workday',
        outcome: 'success',
        detail:
          'Role: Strategic Account Executive. Level: Senior. Start: Jan 13, 2020. Type: FT. Base: $142,000.',
      },
      {
        agentName: 'Letter / Document Generation Agent',
        action: 'Generated signed employment verification letter (PDF)',
        timestamp: ts(-137),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Letter generated on official letterhead. E-signature applied. PDF attached.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Delivered letter to employee via email and closed case',
        timestamp: ts(-136),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail: 'Letter emailed as PDF attachment. Case closed. Audit trail complete.',
      },
    ],
    resolutionSummary:
      'Employment data retrieved from Workday, verification letter auto-generated and delivered within 4 minutes.',
    chatHistory: [
      { id: 'c4-m1', sender: 'employee', senderName: 'Alex Thompson', message: 'Hi, I need an employment verification letter for a mortgage application. My lender needs it to confirm my title, start date, and salary.', timestamp: ts(-140) },
      { id: 'c4-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Alex! I\'ll generate that right away. Let me pull your employment details from Workday.', timestamp: ts(-139) },
      { id: 'c4-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve confirmed your details: Strategic Account Executive, started January 13, 2020, full-time status. Your verification letter has been generated on official company letterhead with an e-signature.', timestamp: ts(-137) },
      { id: 'c4-m4', sender: 'employee', senderName: 'Alex Thompson', message: 'Does it include my salary? The lender specifically needs that.', timestamp: ts(-137) },
      { id: 'c4-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes, it includes your base compensation as authorized by HR policy. The PDF has been sent to your email. Is there anything else you need for the application?', timestamp: ts(-136) },
      { id: 'c4-m6', sender: 'employee', senderName: 'Alex Thompson', message: 'No, that\'s perfect. Thank you for the quick turnaround!', timestamp: ts(-136) },
    ],
    relatedSystems: ['Workday', 'SharePoint Knowledge Base', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 5 — L1 / New (just arrived) ─────────────────────────────────
  {
    id: 'case-005',
    caseRef: 'CASE-2026-0485',
    tier: 'L1',
    type: 'pto_leave_balance',
    status: 'new',
    channel: 'teams',
    submittedAt: ts(-2),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-143',
      name: 'Michael Brown',
      email: 'michael.brown@company.com',
      department: 'Operations',
      location: 'Seattle, WA',
      employeeId: 'EMP-0143',
      manager: 'Tanya Okafor',
      startDate: '2024-02-26',
    },
    subject: 'Can I roll over unused PTO into next year?',
    description:
      'Michael sent a Teams message asking whether unused PTO days carry over into the next calendar year, or if it\'s use-it-or-lose-it by December 31.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-PTO-003',
        policyName: 'PTO Rollover Policy',
        triggered: true,
        outcome: 'pass',
        detail: 'Policy evaluation pending. Will allow up to 5 days rollover.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Case received and classified (L1, auto-resolution queued)',
        timestamp: ts(-2),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Policy lookup in progress. Response generation pending.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Querying PTO rollover eligibility from Workday',
        timestamp: ts(-1),
        systemTouched: 'Workday',
        outcome: 'pending',
        detail: 'HRIS lookup in progress. Checking current PTO balance and rollover eligibility.',
      },
    ],
    chatHistory: [
      { id: 'c5-m1', sender: 'employee', senderName: 'Michael Brown', message: 'Quick question — do unused PTO days carry over into next year or is it use-it-or-lose-it?', timestamp: ts(-2) },
      { id: 'c5-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Michael! Great question. Let me check our PTO rollover policy and your current balance.', timestamp: ts(-1) },
      { id: 'c5-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Good news: the company policy allows up to **5 PTO days** to roll over into the next calendar year. Any unused days beyond 5 are forfeited on December 31. You currently have 8.5 days remaining.', timestamp: ts(-1) },
      { id: 'c5-m4', sender: 'employee', senderName: 'Michael Brown', message: 'So if I have 8.5 left, only 5 will carry over and 3.5 will be lost?', timestamp: ts(-1) },
      { id: 'c5-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Exactly right. If you don\'t use any more PTO before December 31, you\'d roll over 5 days and forfeit 3.5. With about 7 months left in the year, you have time to plan.', timestamp: ts(-1) },
      { id: 'c5-m6', sender: 'employee', senderName: 'Michael Brown', message: 'What if I want to use some of those days before year end? How do I book them?', timestamp: ts(-1) },
      { id: 'c5-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Submit your leave request in Workday: **Time Off → Request → Vacation**. Your manager Tanya Okafor gets an auto-notification. For best results, submit at least 2 weeks in advance.', timestamp: ts(-1) },
      { id: 'c5-m8', sender: 'employee', senderName: 'Michael Brown', message: 'Is there a way to see my PTO usage history?', timestamp: ts(-1) },
      { id: 'c5-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes! In Workday go to **Time Off → View → Time Off Balance and History**. You\'ll see your full accrual and usage record for 2026.', timestamp: ts(-1) },
      { id: 'c5-m10', sender: 'employee', senderName: 'Michael Brown', message: 'Great, that\'s all I needed. Thanks!', timestamp: ts(-1) },
    ],
    relatedSystems: ['Workday', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 6 — L1 / Investigating ───────────────────────────────────────
  {
    id: 'case-006',
    caseRef: 'CASE-2026-0486',
    tier: 'L1',
    type: 'payroll_query',
    status: 'investigating',
    channel: 'workday',
    submittedAt: ts(-55),
    slaDeadline: sla(3),
    slaBreached: false,
    employee: {
      id: 'emp-029',
      name: 'Rachel Kim',
      email: 'rachel.kim@company.com',
      department: 'Engineering',
      location: 'San Francisco, CA',
      employeeId: 'EMP-0029',
      manager: 'Sarah Williams',
      startDate: '2022-06-01',
    },
    subject: 'My June 10 paycheck is $320 less than expected',
    description:
      'Rachel noticed her June 10 net pay was $320 lower than the previous month. She\'s asking for a breakdown explaining the discrepancy. Her base salary has not changed.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-PAY-002',
        policyName: 'Payroll Discrepancy Investigation',
        triggered: true,
        outcome: 'pass',
        detail:
          'Discrepancy under $500 qualifies for autonomous investigation. Above $500 requires specialist.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as payroll query (L1)',
        timestamp: ts(-55),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Amount under threshold. Auto-investigation authorised.',
      },
      {
        agentName: 'Payroll Context Agent',
        action: 'Pulled payslip comparison for May vs June',
        timestamp: ts(-50),
        systemTouched: 'Payroll System',
        outcome: 'success',
        detail: 'May net: $8,247. June net: $7,927. Difference: $320. Dental premium change detected.',
      },
      {
        agentName: 'Benefits Context Agent',
        action: 'Cross-referencing dental plan change with Benefits Portal',
        timestamp: ts(-30),
        systemTouched: 'Benefits Portal',
        outcome: 'pending',
        detail:
          'Verifying open-enrollment dental plan tier upgrade from Basic→Enhanced effective June 1.',
      },
    ],
    chatHistory: [
      { id: 'c6-m1', sender: 'employee', senderName: 'Rachel Kim', message: 'Hi, my June 10 paycheck was about $320 less than last month. Can you tell me why?', timestamp: ts(-55) },
      { id: 'c6-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Rachel! I\'ll investigate right away. Pulling your May and June pay stubs from Workday now.', timestamp: ts(-54) },
      { id: 'c6-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Here\'s the comparison: **May net: $8,247 → June net: $7,927** — a $320 difference. I can see a change in your dental premium line. Let me cross-reference with the Benefits Portal.', timestamp: ts(-50) },
      { id: 'c6-m4', sender: 'employee', senderName: 'Rachel Kim', message: 'Oh — I did upgrade my dental plan during open enrollment. Could that be it?', timestamp: ts(-45) },
      { id: 'c6-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'That\'s almost certainly the cause. I\'m verifying your open-enrollment election: Basic → Enhanced dental tier effective June 1. Checking the exact premium difference now.', timestamp: ts(-40) },
      { id: 'c6-m6', sender: 'ai_agent', senderName: 'AI Agent', message: 'Confirmed: your **Enhanced dental premium** is $320/month more than Basic. The change took effect June 1 as elected. This fully explains the difference in your paycheck — it\'s correct, not an error.', timestamp: ts(-35) },
      { id: 'c6-m7', sender: 'employee', senderName: 'Rachel Kim', message: 'Oh wow, I didn\'t realise the premium difference was that large. Is there anything I can do?', timestamp: ts(-30) },
      { id: 'c6-m8', sender: 'ai_agent', senderName: 'AI Agent', message: 'Outside of Open Enrollment, you can only change your dental plan if you have a qualifying life event (marriage, divorce, birth of a child, loss of other coverage). The next Open Enrollment is November 1–15.', timestamp: ts(-28) },
      { id: 'c6-m9', sender: 'employee', senderName: 'Rachel Kim', message: 'I see. I\'ll keep the Enhanced plan for now — the coverage is better. Can you send me a summary of what the Enhanced plan covers?', timestamp: ts(-25) },
      { id: 'c6-m10', sender: 'ai_agent', senderName: 'AI Agent', message: 'Absolutely — I\'ll send you the 2026 Dental Plan Comparison PDF. It shows Basic vs Enhanced coverage side by side, including preventive, basic, major, and orthodontia coverage tiers.', timestamp: ts(-22) },
      { id: 'c6-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Document sent to your email. Your Enhanced plan includes **100% preventive**, **80% basic**, **50% major**, and **orthodontia up to $2,000 lifetime**. Case resolved — no payroll error found.', timestamp: ts(-20) },
      { id: 'c6-m12', sender: 'employee', senderName: 'Rachel Kim', message: 'Perfect, thanks for the thorough explanation!', timestamp: ts(-18) },
    ],
    relatedSystems: ['Workday', 'Payroll System', 'Benefits Portal', 'ServiceNow'],
  },

  // ── CASE 7 — L2 / Resolved / Approval Required ───────────────────────
  {
    id: 'case-007',
    caseRef: 'CASE-2026-0487',
    tier: 'L2',
    type: 'entitlement_eligibility',
    status: 'resolved',
    channel: 'hris_triggered',
    submittedAt: ts(-320),
    resolvedAt: ts(-310),
    slaDeadline: sla(24),
    slaBreached: false,
    employee: {
      id: 'emp-178',
      name: 'Tom Wilson',
      email: 'tom.wilson@company.com',
      department: 'Product',
      location: 'New York, NY',
      employeeId: 'EMP-0178',
      manager: 'Diana Clarke',
      startDate: '2023-09-18',
    },
    subject: 'Parental leave entitlement (expecting in August)',
    description:
      'Workday triggered this case automatically when Tom updated his "expected family addition" field. Case checks parental leave eligibility, tenure requirements, and initiates entitlement calculation.',
    resolutionPathway: 'approval_required',
    policyTriggers: [
      {
        policyId: 'POL-LEV-004',
        policyName: 'Parental Leave Entitlement',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'Tenure: 2.7 years (meets 1-year minimum). Entitlement: 12 weeks paid. Requires HRBP approval for scheduling.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'HRIS-triggered case created on family addition event',
        timestamp: ts(-320),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Auto-triggered by Workday lifecycle event.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Verified tenure and entitlement eligibility criteria',
        timestamp: ts(-318),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Tenure: 2.7 years. All criteria met. 12 weeks paid leave authorised.',
      },
      {
        agentName: 'Approval Routing Agent',
        action: 'Sent entitlement summary and HRBP scheduling request',
        timestamp: ts(-310),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail: 'Sent leave planning guide and HRBP contact for scheduling discussion.',
      },
    ],
    resolutionSummary:
      'Parental leave eligibility confirmed (12 weeks paid). HRBP notified for scheduling. Employee informed within 10 minutes of HRIS trigger.',
    chatHistory: [
      { id: 'c7-m1', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Tom — this case was automatically triggered when you updated your "expected family addition" field in Workday. First of all, congratulations! 🎉', timestamp: ts(-320) },
      { id: 'c7-m2', sender: 'employee', senderName: 'Tom Wilson', message: 'Thanks! Yes, we\'re expecting in August. I\'d like to understand my parental leave entitlement.', timestamp: ts(-318) },
      { id: 'c7-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve verified your eligibility. With **2.7 years of tenure** (exceeding the 1-year minimum), you qualify for **12 weeks of fully paid parental leave** under the company\'s Enhanced Parental Leave policy.', timestamp: ts(-316) },
      { id: 'c7-m4', sender: 'employee', senderName: 'Tom Wilson', message: 'Can the 12 weeks be split or does it have to be taken all at once?', timestamp: ts(-315) },
      { id: 'c7-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'The 12 weeks can be taken in **one continuous block** or split into a maximum of 2 blocks, as long as all leave is taken within 12 months of the birth or adoption date. Your HRBP will discuss scheduling options.', timestamp: ts(-314) },
      { id: 'c7-m6', sender: 'employee', senderName: 'Tom Wilson', message: 'What about state-level leave? I\'m in New York — does NY PFL apply on top of this?', timestamp: ts(-313) },
      { id: 'c7-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes! New York Paid Family Leave (NY PFL) provides up to 12 weeks at 67% of average weekly wage. The company\'s 12-week paid leave **runs concurrently** with NY PFL, ensuring you receive your full salary without interruption. No additional paperwork needed from you.', timestamp: ts(-312) },
      { id: 'c7-m8', sender: 'employee', senderName: 'Tom Wilson', message: 'That\'s a relief. What do I do next to officially start the process?', timestamp: ts(-311) },
      { id: 'c7-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve sent a scheduling request to your HRBP, Diana Clarke. She\'ll reach out within **2 business days** to confirm your leave dates and file the NY PFL paperwork. I\'ve also sent you the Parental Leave Planning Guide.', timestamp: ts(-310) },
      { id: 'c7-m10', sender: 'employee', senderName: 'Tom Wilson', message: 'Will this affect my equity vesting while I\'m on leave?', timestamp: ts(-309) },
      { id: 'c7-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Great question! Company policy treats paid parental leave as **active service** for equity vesting purposes. Your RSU and option vesting schedule will continue uninterrupted during your 12 weeks.', timestamp: ts(-308) },
      { id: 'c7-m12', sender: 'employee', senderName: 'Tom Wilson', message: 'This is incredibly thorough. Thank you for handling everything so quickly.', timestamp: ts(-310) },
    ],
    relatedSystems: ['Workday', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 8 — L1 / Resolved / Autonomous ──────────────────────────────
  {
    id: 'case-008',
    caseRef: 'CASE-2026-0488',
    tier: 'L1',
    type: 'pto_leave_balance',
    status: 'resolved',
    channel: 'email',
    submittedAt: ts(-400),
    resolvedAt: ts(-394),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-005',
      name: 'Aisha Patel',
      email: 'aisha.patel@company.com',
      department: 'Human Resources',
      location: 'Chicago, IL',
      employeeId: 'EMP-0005',
      manager: 'Greg Monroe',
      startDate: '2023-05-01',
    },
    subject: 'Sick leave vs PTO: which bucket does Monday count against?',
    description:
      'Aisha had an unplanned absence on Monday and is unsure whether it should be coded as sick leave or general PTO in Workday, and whether sick leave is tracked separately.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-PTO-005',
        policyName: 'Leave Type Classification',
        triggered: true,
        outcome: 'pass',
        detail:
          'Illness-related absence codes to Sick Leave bucket. Separate 10-day annual sick leave pool. PTO not impacted.',
      },
    ],
    agentActions: [
      {
        agentName: 'Knowledge Base Agent',
        action: 'Looked up leave type classification policy',
        timestamp: ts(-400),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Policy doc retrieved. Sick leave: 10 days/year, separate from PTO.',
      },
      {
        agentName: 'Transaction Execution Agent',
        action: 'Coded Monday absence as sick leave in Workday',
        timestamp: ts(-396),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Leave type: Sick. Date: June 9. Sick leave balance: 9 days remaining.',
      },
    ],
    resolutionSummary:
      'Leave type clarified, Monday absence correctly coded as sick leave in Workday. PTO balance unaffected.',
    chatHistory: [
      { id: 'c8-m1', sender: 'employee', senderName: 'Aisha Patel', message: 'I was out sick on Monday but I\'m not sure which leave bucket it comes from. Is it PTO or sick leave?', timestamp: ts(-400) },
      { id: 'c8-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Aisha! Illness-related absences are coded under your Sick Leave bucket, which is separate from PTO. You have 10 sick days per year.', timestamp: ts(-399) },
      { id: 'c8-m3', sender: 'employee', senderName: 'Aisha Patel', message: 'Oh good, so my PTO days aren\'t affected?', timestamp: ts(-398) },
      { id: 'c8-m4', sender: 'ai_agent', senderName: 'AI Agent', message: 'Correct! I\'ve coded Monday as sick leave in Workday. Your sick leave balance is now 9 days remaining, and your PTO balance is unchanged.', timestamp: ts(-396) },
      { id: 'c8-m5', sender: 'employee', senderName: 'Aisha Patel', message: 'Great, thanks for clarifying that so quickly.', timestamp: ts(-395) },
    ],
    relatedSystems: ['Workday', 'SharePoint Knowledge Base', 'ServiceNow'],
  },

  // ── CASE 9 — L2 / In Workbench ────────────────────────────────────────
  {
    id: 'case-009',
    caseRef: 'CASE-2026-0489',
    tier: 'L2',
    type: 'accommodation_disability',
    status: 'in_workbench',
    channel: 'email',
    submittedAt: ts(-90),
    slaDeadline: sla(22),
    slaBreached: false,
    employee: {
      id: 'emp-019',
      name: 'Carlos Rivera',
      email: 'carlos.rivera@company.com',
      department: 'Engineering',
      location: 'Bangalore, India',
      employeeId: 'EMP-0019',
      manager: 'Sarah Williams',
      startDate: '2021-04-19',
    },
    subject: 'Requesting ergonomic workstation setup (wrist tendinitis diagnosis)',
    description:
      'Carlos has been diagnosed with bilateral wrist tendinitis by his physician and is requesting a workplace accommodation: an ergonomic keyboard, vertical mouse, monitor stand, and potentially a standing desk. He has attached a physician note.',
    resolutionPathway: 'workbench_escalation',
    policyTriggers: [
      {
        policyId: 'POL-ACC-001',
        policyName: 'Reasonable Accommodation: ADA/PWDDA',
        triggered: true,
        outcome: 'escalate',
        detail:
          'Medical accommodation requests require HR specialist and manager review under ADA interactive process. Cannot be auto-resolved.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as accommodation request (L2, workbench escalation required)',
        timestamp: ts(-90),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail:
          'Medical documentation attached. ADA policy triggered. Specialist Nadia Desai assigned.',
      },
      {
        agentName: 'Letter / Document Generation Agent',
        action: 'Extracted and anonymised physician note for specialist review',
        timestamp: ts(-88),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Diagnosis confirmed: bilateral wrist tendinitis. Equipment list extracted.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Verified ergonomic equipment availability and estimated cost via ServiceNow',
        timestamp: ts(-75),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail:
          'Ergonomic bundle: $847. Within department accommodation budget. Pre-approval not required.',
      },
    ],
    workbenchNote:
      'Specialist review required: Confirm ADA interactive process steps with Carlos and his manager. Verify physician note meets HR documentation standards. Approve equipment order once confirmed.',
    specialistId: 'specialist-001',
    chatHistory: [
      { id: 'c9-m1', sender: 'employee', senderName: 'Carlos Rivera', message: 'I\'ve been diagnosed with bilateral wrist tendinitis and need to request an ergonomic workstation setup. I have a physician\'s note I can share.', timestamp: ts(-90) },
      { id: 'c9-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Carlos, I\'m sorry to hear about your diagnosis. I\'ve received your physician\'s note and am processing your accommodation request under our ADA interactive process policy.', timestamp: ts(-89) },
      { id: 'c9-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Based on the physician\'s recommendations, here\'s the ergonomic equipment I\'ve identified: **ergonomic keyboard**, **vertical mouse**, **monitor stand**, and **adjustable standing desk**. Total cost: $847 — within your department\'s accommodation budget.', timestamp: ts(-85) },
      { id: 'c9-m4', sender: 'employee', senderName: 'Carlos Rivera', message: 'That sounds right. How long will it take to get everything set up?', timestamp: ts(-80) },
      { id: 'c9-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Since this is a medical accommodation, it requires specialist review under the ADA interactive process. I\'ve assigned this to **Nadia Desai (HR Specialist)** who will coordinate the next steps with you and your manager.', timestamp: ts(-78) },
      { id: 'c9-m6', sender: 'specialist', senderName: 'Nadia Desai', message: 'Hi Carlos, I\'ve reviewed your request and the physician\'s note. I\'ll be in touch to schedule a brief meeting to discuss the setup timeline. We aim to have everything in place within 5 business days.', timestamp: ts(-70) },
      { id: 'c9-m7', sender: 'employee', senderName: 'Carlos Rivera', message: 'Thank you both. Is there anything I need to do in the meantime?', timestamp: ts(-68) },
      { id: 'c9-m8', sender: 'specialist', senderName: 'Nadia Desai', message: 'Please complete the ADA Interactive Process form — I\'ve sent it to your email. It\'s a quick 5-minute form. Also, let your manager know you have an accommodation request in progress.', timestamp: ts(-65) },
      { id: 'c9-m9', sender: 'employee', senderName: 'Carlos Rivera', message: 'Done — form submitted. My manager is aware as well.', timestamp: ts(-60) },
      { id: 'c9-m10', sender: 'specialist', senderName: 'Nadia Desai', message: 'Perfect. I\'ve placed the equipment order through ServiceNow. Estimated delivery: 3 business days. A facilities tech will contact you to schedule installation once the items arrive.', timestamp: ts(-55) },
      { id: 'c9-m11', sender: 'employee', senderName: 'Carlos Rivera', message: 'This is great — really appreciate how smoothly this is being handled.', timestamp: ts(-50) },
      { id: 'c9-m12', sender: 'ai_agent', senderName: 'AI Agent', message: 'Case status: equipment ordered, delivery tracking active. I\'ll send you an update when items are received at the office. Nadia will schedule installation immediately after.', timestamp: ts(-48) },
      { id: 'c9-m13', sender: 'employee', senderName: 'Carlos Rivera', message: 'Will there be any follow-up after installation to make sure everything is set up correctly?', timestamp: ts(-45) },
      { id: 'c9-m14', sender: 'specialist', senderName: 'Nadia Desai', message: 'Yes — I\'ll check in with you 5 business days after installation. If anything needs adjustment (height, mouse sensitivity, software settings), we can arrange an ergonomics consultation at no cost.', timestamp: ts(-42) },
    ],
    relatedSystems: ['Workday', 'ServiceNow', 'SharePoint Knowledge Base'],
  },

  // ── CASE 10 — L2 / In Workbench ───────────────────────────────────────
  {
    id: 'case-010',
    caseRef: 'CASE-2026-0490',
    tier: 'L2',
    type: 'exception_appeal',
    status: 'in_workbench',
    channel: 'workday',
    submittedAt: ts(-120),
    slaDeadline: sla(18),
    slaBreached: false,
    employee: {
      id: 'emp-001',
      name: 'Ananya Iyer',
      email: 'ananya.iyer@company.com',
      department: 'Engineering',
      location: 'Bangalore, India',
      employeeId: 'EMP-0001',
      manager: 'Sarah Williams',
      startDate: '2022-01-10',
    },
    subject: 'Appeal: Request to carry over 8 PTO days (above policy 5-day cap)',
    description:
      'Ananya is requesting a one-time exception to carry over 8 PTO days into 2027, above the standard 5-day policy cap. She states that 6 days were unused due to two project deliveries she had to cover for departed colleagues in Q4 2026, and she has documented evidence of the project circumstances.',
    resolutionPathway: 'workbench_escalation',
    policyTriggers: [
      {
        policyId: 'POL-PTO-003',
        policyName: 'PTO Rollover Policy',
        triggered: true,
        outcome: 'escalate',
        detail:
          'Policy cap: 5 days. Employee requesting 8 days. Exception requires HRBP approval and VP sign-off.',
      },
      {
        policyId: 'POL-EXC-002',
        policyName: 'Policy Exception Appeal Process',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'Exception appeal documented with supporting evidence. Meets criteria for specialist review.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as policy exception appeal (L2)',
        timestamp: ts(-120),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Policy exception confirmed. Evidence package assembled for specialist.',
      },
      {
        agentName: 'Policy Research Agent',
        action: 'Retrieved PTO rollover policy and assembled exception evidence package',
        timestamp: ts(-115),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Policy cap: 5 days. Appeal basis: Q4 project coverage. Evidence: Workday time records + project documentation.',
      },
      {
        agentName: 'Approval Routing Agent',
        action: 'Escalated exception appeal to HR Specialist and VP Engineering',
        timestamp: ts(-105),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Approval chain: HR Specialist → VP Engineering. Both notified via Teams.',
      },
    ],
    chatHistory: [
      { id: 'c10-m1', sender: 'employee', senderName: 'Ananya Iyer', message: 'I\'d like to request an exception to carry over 8 PTO days instead of the 5-day cap. I couldn\'t take time off due to project coverage obligations in Q4.', timestamp: ts(-120) },
      { id: 'c10-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Ananya, I see this exceeds the standard 5-day rollover cap. I\'ll assemble an exception appeal package. Can you describe the circumstances that prevented you from taking time off?', timestamp: ts(-118) },
      { id: 'c10-m3', sender: 'employee', senderName: 'Ananya Iyer', message: 'Two colleagues resigned in Q4 2026 and I had to cover their project deliveries. I have the project documentation and Workday time records showing the additional coverage.', timestamp: ts(-116) },
      { id: 'c10-m4', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve verified your Q4 project coverage via Workday time records. Your appeal circumstances are substantiated. I\'m compiling the exception package with policy excerpts and your supporting evidence now.', timestamp: ts(-110) },
      { id: 'c10-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Appeal package assembled and escalated to an HR Specialist. This requires **HRBP + VP Engineering sign-off**. Standard turnaround is 2 business days. I\'ll notify you as each approval comes in.', timestamp: ts(-105) },
      { id: 'c10-m6', sender: 'specialist', senderName: 'HR Specialist', message: 'Hi Ananya, I\'ve reviewed your appeal and all supporting evidence checks out. The Q4 coverage circumstances are well-documented. I\'m recommending approval and have forwarded to VP Engineering for final sign-off.', timestamp: ts(-95) },
      { id: 'c10-m7', sender: 'employee', senderName: 'Ananya Iyer', message: 'Thank you! How will I be notified of the VP\'s decision?', timestamp: ts(-90) },
      { id: 'c10-m8', sender: 'specialist', senderName: 'HR Specialist', message: 'You\'ll receive an email notification from HR once VP Engineering approves. I expect a response within 1-2 business days. The AI Agent will also update your case status here automatically.', timestamp: ts(-88) },
      { id: 'c10-m9', sender: 'employee', senderName: 'Ananya Iyer', message: 'Will the rollover happen automatically once approved, or do I need to do something in Workday?', timestamp: ts(-85) },
      { id: 'c10-m10', sender: 'specialist', senderName: 'HR Specialist', message: 'Once approved, I\'ll update Workday directly. The rollover balance will appear in your 2027 leave account on January 2nd. No action needed from you.', timestamp: ts(-82) },
      { id: 'c10-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'VP Engineering approval received. Case outcome: **Exception approved — 8-day rollover authorized**. I\'ve notified Ananya via email and updated the Workday rollover balance. Case closed.', timestamp: ts(-75) },
      { id: 'c10-m12', sender: 'employee', senderName: 'Ananya Iyer', message: 'Fantastic! Thank you for handling this so thoroughly. I really appreciate it.', timestamp: ts(-74) },
      { id: 'c10-m13', sender: 'specialist', senderName: 'HR Specialist', message: 'Happy to help, Ananya. We hope you\'ll have more time to rest in 2027 — you\'ve earned it! The 8 days will be visible in Workday from January 2nd.', timestamp: ts(-72) },
      { id: 'c10-m14', sender: 'employee', senderName: 'Ananya Iyer', message: 'Thank you both so much. This process was really transparent and well-managed.', timestamp: ts(-70) },
    ],
    relatedSystems: ['Workday', 'ServiceNow', 'SharePoint Knowledge Base'],
  },

  // ── CASE 11 — L2 / Awaiting Approval ─────────────────────────────────
  {
    id: 'case-011',
    caseRef: 'CASE-2026-0491',
    tier: 'L2',
    type: 'multi_system_transaction',
    status: 'awaiting_approval',
    channel: 'hris_triggered',
    submittedAt: ts(-75),
    slaDeadline: sla(24),
    slaBreached: false,
    employee: {
      id: 'emp-089',
      name: 'James Martinez',
      email: 'james.martinez@company.com',
      department: 'Finance',
      location: 'New York, NY',
      employeeId: 'EMP-0089',
      manager: 'Linda Nguyen',
      startDate: '2020-07-06',
    },
    subject: 'Equity grant RSU correction (incorrect vesting start date)',
    description:
      'James was granted 2,000 RSUs on his 4-year anniversary in July 2024. A data entry error in the equity system shows the vesting start date as October 2024 instead of July 2024, meaning 3 months of vesting have not been registered. This multi-system correction touches Workday, the equity management platform, and Payroll.',
    resolutionPathway: 'approval_required',
    policyTriggers: [
      {
        policyId: 'POL-EQU-001',
        policyName: 'Equity Grant Correction',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'RSU corrections require Finance Controller and Legal review. Multi-system update path confirmed.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Case auto-triggered from equity system discrepancy flag',
        timestamp: ts(-75),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Grant ID: GRANT-2024-0342. Discrepancy: 3-month vesting offset detected.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Calculated RSU vesting shortfall from Workday equity records',
        timestamp: ts(-70),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'RSU vesting shortfall: 125 units (3 months × ~41.67 units/month). Estimated value: $18,750 at current price.',
      },
      {
        agentName: 'Approval Routing Agent',
        action: 'Sent approval package to Finance Controller and Legal',
        timestamp: ts(-60),
        systemTouched: 'ServiceNow',
        outcome: 'pending',
        detail: 'Approval request delivered to Linda Nguyen (Controller) and Marcus Webb (Legal). Awaiting sign-off.',
      },
    ],
    chatHistory: [
      { id: 'c11-m1', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi James — this case was auto-triggered by an equity system discrepancy flag on your grant **GRANT-2024-0342**. I\'ve detected a 3-month vesting offset that needs to be corrected.', timestamp: ts(-75) },
      { id: 'c11-m2', sender: 'employee', senderName: 'James Martinez', message: 'Yes, I noticed this. My vesting start date shows October 2024 but the grant was issued in July 2024 when I hit my 4-year anniversary.', timestamp: ts(-72) },
      { id: 'c11-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Confirmed. The vesting shortfall is **125 RSU units** (3 months × ~41.67 units/month), estimated value **~$18,750** at the current share price. This is a multi-system correction: Workday, Equity Platform, and Payroll all need updating.', timestamp: ts(-68) },
      { id: 'c11-m4', sender: 'employee', senderName: 'James Martinez', message: 'Who needs to approve this? I want to make sure it gets resolved quickly.', timestamp: ts(-65) },
      { id: 'c11-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve sent the full approval package to **Linda Nguyen (Finance Controller)** and **Marcus Webb (Legal)**. Both need to sign off due to the financial materiality. I\'ll update you as each approval comes in.', timestamp: ts(-60) },
      { id: 'c11-m6', sender: 'employee', senderName: 'James Martinez', message: 'How long does this usually take?', timestamp: ts(-55) },
      { id: 'c11-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Typically 2-3 business days for both approvals. Finance Controller usually responds within 24 hours; Legal review takes 24-48 hours. I\'ll send you a Teams notification the moment each approval is received.', timestamp: ts(-52) },
      { id: 'c11-m8', sender: 'employee', senderName: 'James Martinez', message: 'Once both approve, what happens next?', timestamp: ts(-48) },
      { id: 'c11-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Once both approvals are in, I\'ll simultaneously update: (1) Workday grant record — vesting start date corrected to July 2024, (2) Equity Platform — 125 catch-up units processed, (3) Payroll — any tax withholding implications handled. You\'ll receive a corrected RSU certificate by email.', timestamp: ts(-45) },
      { id: 'c11-m10', sender: 'employee', senderName: 'James Martinez', message: 'Will those 125 catch-up units vest immediately or on the next scheduled date?', timestamp: ts(-40) },
      { id: 'c11-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Legal has confirmed: the 125 units will vest immediately upon system correction, as they represent past service already rendered. They\'ll appear in your equity dashboard within 24 hours of the correction being applied.', timestamp: ts(-35) },
    ],
    relatedSystems: ['Workday', 'Payroll System', 'ServiceNow'],
  },

  // ── CASE 12 — L2 / Escalated ──────────────────────────────────────────
  {
    id: 'case-012',
    caseRef: 'CASE-2026-0492',
    tier: 'L2',
    type: 'sensitive_employee_relations',
    status: 'escalated',
    channel: 'portal',
    submittedAt: ts(-480),
    slaDeadline: sla(2),
    slaBreached: false,
    employee: {
      id: 'emp-confidential',
      name: 'Confidential (Anonymous Submission)',
      email: 'anonymous@company.com',
      department: 'Sales',
      location: 'Austin, TX',
      employeeId: 'ANON',
      manager: 'Undisclosed',
      startDate: 'Undisclosed',
    },
    subject: 'Formal complaint: persistent hostile comments from team lead',
    description:
      'An anonymous complaint was submitted through the HR portal. The employee describes a pattern of hostile, demeaning comments directed at them by their team lead over the past 6 weeks. Three specific incidents are described with dates and witnesses named.',
    resolutionPathway: 'workbench_escalation',
    policyTriggers: [
      {
        policyId: 'POL-ER-001',
        policyName: 'Harassment and Hostile Work Environment',
        triggered: true,
        outcome: 'escalate',
        detail:
          'Formal complaint with named witnesses. Mandatory HRBP + Legal involvement under Code of Conduct. Cannot be auto-processed.',
      },
      {
        policyId: 'POL-ER-003',
        policyName: 'Anonymous Complaint Handling',
        triggered: true,
        outcome: 'escalate',
        detail:
          'Anonymous submissions route directly to HRBP and ER Specialist. No AI autonomous actions permitted.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Case received and immediately flagged as Sensitive ER (L2)',
        timestamp: ts(-480),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail:
          'Content scan detected harassment keywords. Auto-escalation protocol triggered. Zero AI autonomous actions taken.',
      },
      {
        agentName: 'Approval Routing Agent',
        action: 'Immediately notified HRBP and ER Specialist via Teams',
        timestamp: ts(-479),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail:
          'Elena Vasquez (HRBP) and Robert Chen (ER Specialist) notified within 60 seconds of submission. Case locked for human-only handling.',
      },
    ],
    workbenchNote:
      'HUMAN-ONLY CASE. No further AI actions. Complaint involves named witnesses and specific incident dates. Formal investigation protocol initiated by Elena Vasquez (HRBP). Legal informed.',
    specialistId: 'specialist-003',
    chatHistory: [
      { id: 'c12-m1', sender: 'employee', senderName: 'Anonymous', message: 'I need to report something confidentially.', timestamp: ts(-480) },
      { id: 'c12-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Your submission is completely confidential. Please describe the situation you\'d like to report. I\'m here to listen.', timestamp: ts(-479) },
      { id: 'c12-m3', sender: 'employee', senderName: 'Anonymous', message: 'My team lead has been making hostile, demeaning comments to me over the past 6 weeks. There are three specific incidents I want to document.', timestamp: ts(-478) },
      { id: 'c12-m4', sender: 'employee', senderName: 'Anonymous', message: 'The first incident was on May 1st during a team standup. The second was May 15th in a 1:1 meeting. The third was May 28th in a group chat — there were witnesses.', timestamp: ts(-477) },
      { id: 'c12-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'I understand this is a serious and difficult situation. I want to assure you that this will be handled with the utmost care, professionalism, and confidentiality throughout.', timestamp: ts(-476) },
      { id: 'c12-m6', sender: 'ai_agent', senderName: 'AI Agent', message: 'This case has been classified as **Sensitive Employee Relations (L2)**. I am routing it directly to our HRBP and Employee Relations Specialist. **No AI autonomous actions will be taken** — this is human-only handling.', timestamp: ts(-476) },
      { id: 'c12-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Your complaint has been assigned **CASE-2026-0492**. Elena Vasquez (HRBP) and Robert Chen (ER Specialist) have been notified within 60 seconds of your submission. They will contact you within 24 hours.', timestamp: ts(-475) },
      { id: 'c12-m8', sender: 'employee', senderName: 'Anonymous', message: 'Thank you. Please make sure my identity stays completely anonymous throughout this entire process.', timestamp: ts(-474) },
      { id: 'c12-m9', sender: 'specialist', senderName: 'Elena Vasquez (HRBP)', message: 'Hello — your anonymity is protected by our formal investigation protocol. No details identifying you will be shared with any other parties, including your team lead, without your explicit consent. This is a legal obligation, not just policy.', timestamp: ts(-460) },
      { id: 'c12-m10', sender: 'employee', senderName: 'Anonymous', message: 'I\'m relieved to hear that. What happens next?', timestamp: ts(-455) },
      { id: 'c12-m11', sender: 'specialist', senderName: 'Elena Vasquez (HRBP)', message: 'Robert Chen (ER Specialist) and I will conduct an independent investigation. We will interview the named witnesses separately and review the group chat logs from May 28th. We\'ll have an initial update for you within 5 business days.', timestamp: ts(-450) },
      { id: 'c12-m12', sender: 'employee', senderName: 'Anonymous', message: 'OK. I\'m glad this is being taken seriously. I\'ll wait to hear from you.', timestamp: ts(-445) },
    ],
    relatedSystems: ['ServiceNow', 'Microsoft Teams'],
  },

  // ── CASE 13 — L1 / Resolved / Autonomous (General HR FAQ) ────────────
  {
    id: 'case-013',
    caseRef: 'CASE-2026-0493',
    tier: 'L1',
    type: 'general_hr_faq',
    status: 'resolved',
    channel: 'teams',
    submittedAt: ts(-260),
    resolvedAt: ts(-256),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-201',
      name: 'Nina Patel',
      email: 'nina.patel@company.com',
      department: 'Product',
      location: 'San Francisco, CA',
      employeeId: 'EMP-0201',
      manager: 'Diana Clarke',
      startDate: '2024-09-16',
    },
    subject: 'What is the company remote work policy?',
    description:
      'Nina asked via Teams whether the company offers fully remote, hybrid, or in-office only arrangements, and what the approval process is for remote work.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-FAQ-001',
        policyName: 'HR FAQ: Remote Work Policy',
        triggered: true,
        outcome: 'pass',
        detail:
          'Company policy: hybrid (3 days office / 2 remote). Full remote requires VP approval. Retrieved from Employee Handbook v2026.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as General HR FAQ (L1)',
        timestamp: ts(-260),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'FAQ category detected. Routed to knowledge base semantic search.',
      },
      {
        agentName: 'Knowledge Base Agent',
        action: 'Retrieved remote work policy from SharePoint Knowledge Base',
        timestamp: ts(-258),
        systemTouched: 'SharePoint Knowledge Base',
        outcome: 'success',
        detail: 'Doc: Employee Handbook v2026, Section 4.3 — Flexible Work Arrangements. Confidence: 0.98.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Sent policy summary to employee via Teams and closed case',
        timestamp: ts(-256),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail:
          'Response: "Hi Nina, our standard arrangement is hybrid (3 office / 2 remote). Full remote requires VP approval via the Remote Work Request form in the HR portal. Let me know if you need the link!"',
      },
    ],
    resolutionSummary:
      'Remote work policy retrieved from knowledge base, plain-language summary delivered via Teams within 4 minutes.',
    chatHistory: [
      { id: 'c13-m1', sender: 'employee', senderName: 'Nina Patel', message: 'Hi, what\'s the company\'s remote work policy? Can I work fully remote or is it hybrid?', timestamp: ts(-260) },
      { id: 'c13-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Nina! Our standard arrangement is hybrid: 3 days in the office and 2 days remote per week.', timestamp: ts(-259) },
      { id: 'c13-m3', sender: 'employee', senderName: 'Nina Patel', message: 'Is it possible to go fully remote?', timestamp: ts(-258) },
      { id: 'c13-m4', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes, full remote is possible but requires VP approval. You\'d need to submit a Remote Work Request form through the HR portal. I can send you the link if you\'d like.', timestamp: ts(-257) },
      { id: 'c13-m5', sender: 'employee', senderName: 'Nina Patel', message: 'Yes please, send me the link.', timestamp: ts(-257) },
      { id: 'c13-m6', sender: 'ai_agent', senderName: 'AI Agent', message: 'Here\'s the link to the Remote Work Request form: [HR Portal > Forms > Remote Work Request]. This info comes from Employee Handbook v2026, Section 4.3 — Flexible Work Arrangements. Anything else I can help with?', timestamp: ts(-256) },
      { id: 'c13-m7', sender: 'employee', senderName: 'Nina Patel', message: 'No, that\'s everything. Thank you!', timestamp: ts(-256) },
    ],
    relatedSystems: ['SharePoint Knowledge Base', 'Microsoft Teams', 'ServiceNow'],
  },

  // ── CASE 14 — L2 / Investigating (Performance Data Request) ──────────
  {
    id: 'case-014',
    caseRef: 'CASE-2026-0494',
    tier: 'L2',
    type: 'performance_data_request',
    status: 'investigating',
    channel: 'portal',
    submittedAt: ts(-45),
    slaDeadline: sla(24),
    slaBreached: false,
    employee: {
      id: 'emp-156',
      name: 'Derek Chang',
      email: 'derek.chang@company.com',
      department: 'Engineering',
      location: 'Austin, TX',
      employeeId: 'EMP-0156',
      manager: 'James Caldwell',
      startDate: '2021-11-01',
    },
    subject: 'Request access to my full performance review history',
    description:
      'Derek submitted a request via the HR portal to download his complete performance review history (2022-2026) including manager comments, ratings, and development plans. He mentions he needs it for an internal transfer application.',
    resolutionPathway: 'approval_required',
    policyTriggers: [
      {
        policyId: 'POL-PERF-001',
        policyName: 'Performance Data Access Governance',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'Self-access to performance data is permitted. Third-party flag check required before release. Manager notification pending.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as Performance Data Request (L2)',
        timestamp: ts(-45),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Data access request detected. Access governance policy triggered. L2 classification due to data sensitivity.',
      },
      {
        agentName: 'HRIS Data Agent',
        action: 'Retrieved performance review records from Workday',
        timestamp: ts(-40),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: '4 annual reviews found (2022-2025). 2026 mid-year review in progress. All self-access eligible.',
      },
      {
        agentName: 'Compliance Check Agent',
        action: 'Evaluating third-party data flag and access policy',
        timestamp: ts(-35),
        systemTouched: 'ServiceNow',
        outcome: 'pending',
        detail: 'Checking if any review contains third-party feedback that requires redaction before release. Manager notification queued.',
      },
    ],
    chatHistory: [
      { id: 'c14-m1', sender: 'employee', senderName: 'Derek Chang', message: 'I need to download my complete performance review history from 2022-2026 for an internal transfer application.', timestamp: ts(-45) },
      { id: 'c14-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Derek! I can help with that. Let me retrieve your performance review records from Workday.', timestamp: ts(-44) },
      { id: 'c14-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve found 4 completed annual reviews (2022-2025) and your 2026 mid-year review is currently in progress. Since this is a self-access request, it\'s permitted under our access governance policy.', timestamp: ts(-40) },
      { id: 'c14-m4', sender: 'employee', senderName: 'Derek Chang', message: 'Great. Will I get the full reviews including manager comments and ratings?', timestamp: ts(-38) },
      { id: 'c14-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'m running a compliance check first. I need to verify whether any reviews contain third-party feedback that requires redaction before release. This is standard for data access governance.', timestamp: ts(-35) },
      { id: 'c14-m6', sender: 'employee', senderName: 'Derek Chang', message: 'Understood. How long will the compliance check take?', timestamp: ts(-33) },
      { id: 'c14-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'The check typically completes within a few hours. Your manager will also be notified of the data access request as per policy. I\'ll update you as soon as the review is clear.', timestamp: ts(-32) },
    ],
    relatedSystems: ['Workday', 'ServiceNow'],
  },

  // ── CASE 15 — L1 / Resolved / Advocate Health (Night Shift RN) ───────────
  {
    id: 'case-015',
    caseRef: 'CASE-2026-0495',
    tier: 'L1',
    type: 'payroll_query',
    status: 'resolved',
    channel: 'teams',
    submittedAt: ts(-682),
    resolvedAt: ts(-679),
    slaDeadline: sla(4),
    slaBreached: false,
    employee: {
      id: 'emp-ah-301',
      name: 'James Walker',
      email: 'james.walker@advocatehealth.com',
      department: 'Clinical — Nursing (Night Shift)',
      location: 'Advocate Lutheran General Hospital, Park Ridge, WI',
      employeeId: 'EMP-AH-0301',
      manager: 'Charge Nurse Michelle Torres',
      startDate: '2019-08-15',
    },
    subject: 'June 15 paycheck is $285 less — why?',
    description:
      'James Walker, RN on the Night Shift at Advocate Lutheran General Hospital in Wisconsin, noticed his June 15 paycheck was $285 less than May. He reached out at 11:22pm via Teams while finishing his shift. The discrepancy was caused by the Wisconsin state income tax table update effective June 1, 2026.',
    resolutionPathway: 'autonomous',
    policyTriggers: [
      {
        policyId: 'POL-AH-EMP-02',
        policyName: 'Payroll Query — Pay Slip, Deduction, or Overtime Question',
        triggered: true,
        outcome: 'pass',
        detail:
          'Discrepancy $285 — under $500 autonomous threshold. Identified as WI state tax table update effective June 1. No specialist required.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'Classified as payroll query (L1) — Advocate Health WI site',
        timestamp: ts(-682),
        systemTouched: 'ServiceNow',
        outcome: 'success',
        detail: 'Night shift submission at 11:22pm. Same 24/7 SLA applies. Auto-investigation authorized.',
      },
      {
        agentName: 'Payroll Context Agent',
        action: 'Pulled May and June pay stubs for James Walker from Workday',
        timestamp: ts(-681),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'May net: $5,847. June net: $5,562. Difference: $285. WI state income tax line change detected.',
      },
      {
        agentName: 'Compliance Check Agent',
        action: 'Verified WI state tax table update effective June 1, 2026',
        timestamp: ts(-680),
        systemTouched: 'Payroll Compliance DB',
        outcome: 'success',
        detail: 'WI DOR updated withholding tables June 1, 2026. Change is correct and legally mandated.',
      },
      {
        agentName: 'ITSM Ticket Agent',
        action: 'Sent pay stub breakdown and explanation to James via Teams and closed case',
        timestamp: ts(-679),
        systemTouched: 'Microsoft Teams',
        outcome: 'success',
        detail: 'Resolution: paycheck correct. WI tax table change explained. Case closed in 3m 42s.',
      },
    ],
    resolutionSummary:
      'Pay discrepancy identified as WI state income tax table update effective June 1. Paycheck is correct. Case resolved autonomously in 3m 42s at 11:26pm.',
    chatHistory: [
      { id: 'c15-m1', sender: 'employee', senderName: 'James Walker', message: 'Hey — just finished my night shift and noticed my June 15 paycheck is $285 less than last month. Is something wrong?', timestamp: ts(-682) },
      { id: 'c15-m2', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi James! No worries — I\'m on it even at 11:22pm. Let me pull your May and June pay stubs from Workday right now.', timestamp: ts(-682) },
      { id: 'c15-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'Got your stubs. **May net: $5,847 → June net: $5,562** — a $285 difference. I can see the change is in your Wisconsin state income tax line. Investigating now.', timestamp: ts(-681) },
      { id: 'c15-m4', sender: 'employee', senderName: 'James Walker', message: 'My base pay didn\'t change, so that\'s weird. What changed on the WI line?', timestamp: ts(-681) },
      { id: 'c15-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'Found it. The **Wisconsin Department of Revenue updated its state income tax withholding tables effective June 1, 2026**. This is a state-mandated change — your employer is legally required to apply it.', timestamp: ts(-680) },
      { id: 'c15-m6', sender: 'employee', senderName: 'James Walker', message: 'So my paycheck isn\'t wrong — the state just takes more now?', timestamp: ts(-680) },
      { id: 'c15-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'Exactly. Your paycheck is **100% correct**. The WI tax rate bracket for your income level was adjusted upward by the state. This isn\'t an Advocate Health payroll error.', timestamp: ts(-680) },
      { id: 'c15-m8', sender: 'employee', senderName: 'James Walker', message: 'Can you show me exactly where the difference is in the pay stub?', timestamp: ts(-680) },
      { id: 'c15-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Here\'s the breakdown:\n• **Gross pay:** $7,750 (same as May)\n• **Federal tax:** $1,162 (same)\n• **WI state tax (May):** $423 → **WI state tax (June):** $708 (+$285)\n• **FICA/Medicare:** $593 (same)\n• **Benefit deductions:** $725 (same)\n• **Net:** $5,562', timestamp: ts(-680) },
      { id: 'c15-m10', sender: 'employee', senderName: 'James Walker', message: 'Whoa — the WI state tax jumped from $423 to $708? That\'s a big increase.', timestamp: ts(-679) },
      { id: 'c15-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Yes, it\'s a significant table change for your income bracket. If you want to adjust your WI withholding to a lower amount, you can submit a new **WT-4 form** (Wisconsin Employee\'s Withholding Exemption Certificate) to Payroll. Want me to send you the form link?', timestamp: ts(-679) },
      { id: 'c15-m12', sender: 'employee', senderName: 'James Walker', message: 'Yes please. And can you send me a PDF summary of this explanation for my records?', timestamp: ts(-679) },
      { id: 'c15-m13', sender: 'ai_agent', senderName: 'AI Agent', message: 'Done! I\'ve sent both to your Advocate Health email:\n1. WT-4 form link\n2. Pay stub comparison PDF with the WI tax change highlighted\n\nYour case is resolved. Hope the rest of your night goes smoothly, James! 🌙', timestamp: ts(-679) },
      { id: 'c15-m14', sender: 'employee', senderName: 'James Walker', message: 'That was incredibly fast. Thank you for taking care of this at midnight. Really appreciate it.', timestamp: ts(-679) },
    ],
    relatedSystems: ['Workday', 'Microsoft Teams', 'ServiceNow', 'Payroll Compliance DB'],
  },

  // ── CASE 16 — L2 / In Workbench / Advocate Health (Clinical Exit) ────────
  {
    id: 'case-016',
    caseRef: 'CASE-2026-0496',
    tier: 'L2',
    type: 'multi_system_transaction',
    status: 'in_workbench',
    channel: 'hris_triggered',
    submittedAt: ts(-1440),
    slaDeadline: sla(24),
    slaBreached: false,
    employee: {
      id: 'emp-ah-417',
      name: 'Sarah Mitchell',
      email: 'sarah.mitchell@advocatehealth.com',
      department: 'Clinical — Nursing (RN)',
      location: 'Advocate Aurora Medical Center, Milwaukee, WI',
      employeeId: 'EMP-AH-0417',
      manager: 'Nursing Director Lisa Hendricks',
      startDate: '2018-03-22',
    },
    subject: 'Clinical Exit Coordination — Last Working Day June 30',
    description:
      'Sarah Mitchell, RN at Advocate Aurora Medical Center in Milwaukee, submitted her voluntary resignation. Last working day: June 30, 2026. This case was auto-triggered by Workday separation event. Policy POL-AH-EX-01 requires clinical access revocation across EHR (Epic), medication management (Pyxis), and patient scheduling effective July 1. POL-AH-EX-02 coordinates 8-system offboarding. POL-AH-EX-03 handles WI final pay calculation.',
    resolutionPathway: 'workbench_escalation',
    policyTriggers: [
      {
        policyId: 'POL-AH-EX-01',
        policyName: 'Resignation Confirmed — Multi-System Offboarding Trigger',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'EHR (Epic), Pyxis, patient scheduling — all revoked effective July 1 00:00:00. Timestamped HIPAA audit entries generated for each system.',
      },
      {
        policyId: 'POL-AH-EX-02',
        policyName: 'COBRA & Benefits Continuation — Compliance-Driven Notification',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'All 8 offboarding systems queued: AD, Workday, Benefits (COBRA), Payroll, Parking/Badge, IT Equipment, 401k, Email.',
      },
      {
        policyId: 'POL-AH-EX-03',
        policyName: 'Final Pay Calculation — Data Assembly for Payroll Specialist',
        triggered: true,
        outcome: 'approval_required',
        detail:
          'Wisconsin: final pay due on next regular payday after LWD. PTO payout: WI law requires unused accrued PTO to be paid. Specialist review required for accrued balance calculation.',
      },
    ],
    agentActions: [
      {
        agentName: 'Intake & Classification Agent',
        action: 'HRIS-triggered separation case opened on resignation event',
        timestamp: ts(-1440),
        systemTouched: 'Workday',
        outcome: 'success',
        detail: 'Separation type: Voluntary Resignation. LWD: June 30, 2026. Clinical site confirmed: Advocate Aurora Milwaukee.',
      },
      {
        agentName: 'Clinical Access Revocation Agent',
        action: 'Scheduled EHR, Pyxis, and scheduling access revocation for July 1 00:00:00',
        timestamp: ts(-1438),
        systemTouched: 'Epic EHR, Pyxis, Patient Scheduling',
        outcome: 'success',
        detail: 'Revocation tickets raised. HIPAA audit entries timestamped. IT Security notified.',
      },
      {
        agentName: 'Offboarding Coordination Agent',
        action: 'Queued all 8 standard offboarding system actions',
        timestamp: ts(-1436),
        systemTouched: 'Active Directory, Benefits Portal, Payroll, ServiceNow',
        outcome: 'success',
        detail: 'AD deactivation June 30 EOD, COBRA notice generated, IT equipment return label created (laptop).',
      },
      {
        agentName: 'Approval Routing Agent',
        action: 'Escalated to HR Specialist for final pay WI calculation approval',
        timestamp: ts(-1430),
        systemTouched: 'ServiceNow',
        outcome: 'pending',
        detail: 'WI PTO payout calculation requires specialist confirmation. Estimated final pay package sent to HR Ops.',
      },
    ],
    workbenchNote:
      'Clinical access revocations are scheduled and confirmed. HIPAA audit trail complete. Pending: specialist approval of WI final pay calculation including accrued PTO payout ($3,840). Please review and approve to release final payroll processing.',
    specialistId: 'specialist-004',
    chatHistory: [
      { id: 'c16-m1', sender: 'ai_agent', senderName: 'AI Agent', message: 'Hi Sarah — this case was automatically opened when your resignation was processed in Workday. I\'m here to help coordinate your exit from Advocate Aurora Medical Center. Your last working day is confirmed as **June 30, 2026**.', timestamp: ts(-1440) },
      { id: 'c16-m2', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Thanks. I wasn\'t sure what to expect from the HR side. What happens now?', timestamp: ts(-1438) },
      { id: 'c16-m3', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve started the clinical exit coordination under our HIPAA compliance policy. Here\'s what\'s happening automatically:\n\n• **Epic EHR access** → revoked July 1, 00:00:00\n• **Pyxis (medication management)** → revoked July 1, 00:00:00\n• **Patient scheduling system** → revoked July 1, 00:00:00\n\nAll revocations are timestamped and logged to the HIPAA audit trail.', timestamp: ts(-1437) },
      { id: 'c16-m4', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Good — I want to make sure patient access is handled properly for HIPAA. Is there anything I need to sign off on?', timestamp: ts(-1435) },
      { id: 'c16-m5', sender: 'ai_agent', senderName: 'AI Agent', message: 'The clinical access revocations are fully automated and don\'t require your signature. However, IT will send you a **HIPAA exit attestation form** to sign by June 28. I\'ve flagged that to IT Security.', timestamp: ts(-1434) },
      { id: 'c16-m6', sender: 'employee', senderName: 'Sarah Mitchell', message: 'What about my laptop? I\'ve been working remotely for some shifts.', timestamp: ts(-1433) },
      { id: 'c16-m7', sender: 'ai_agent', senderName: 'AI Agent', message: 'I\'ve generated a **prepaid return shipping label** for your Advocate Health laptop. It\'s been sent to your Advocate email. Please ship it within 5 business days of your last day (by July 7).', timestamp: ts(-1432) },
      { id: 'c16-m8', sender: 'employee', senderName: 'Sarah Mitchell', message: 'What about my final paycheck? I have some accrued PTO I haven\'t used.', timestamp: ts(-1430) },
      { id: 'c16-m9', sender: 'ai_agent', senderName: 'AI Agent', message: 'Under **Wisconsin state law**, your employer must pay out all accrued, unused PTO in your final paycheck. I\'ve calculated your accrued balance as **12.8 days ($3,840)**. This requires specialist review and approval before it\'s released to payroll.', timestamp: ts(-1429) },
      { id: 'c16-m10', sender: 'employee', senderName: 'Sarah Mitchell', message: 'When will I receive the final paycheck?', timestamp: ts(-1428) },
      { id: 'c16-m11', sender: 'ai_agent', senderName: 'AI Agent', message: 'Wisconsin law requires final pay on the **next regular payday** after your last day. Your regular payday is every other Friday, so your final check (including PTO payout) will be issued on **July 11, 2026**.', timestamp: ts(-1427) },
      { id: 'c16-m12', sender: 'specialist', senderName: 'HR Ops Specialist', message: 'Hi Sarah — I\'ve reviewed your PTO balance and WI final pay calculation. Everything looks correct: 12.8 days @ $300/day = $3,840. I\'m approving this for payroll processing now. You\'ll receive a final pay summary by July 8.', timestamp: ts(-1380) },
      { id: 'c16-m13', sender: 'employee', senderName: 'Sarah Mitchell', message: 'Thank you both. It\'s been a great 8 years at Advocate Aurora and I\'m glad the exit process has been this organized. Really appreciate the professionalism.', timestamp: ts(-1375) },
    ],
    relatedSystems: ['Workday', 'Epic EHR', 'Pyxis', 'Active Directory', 'ServiceNow', 'Benefits Portal'],
  },
]

// ── Queue Metrics ──────────────────────────────────────────────────────────
//
// THERE IS NO METRICS OBJECT HERE ANY MORE, and that is the point.
//
// This file used to export a `mockQueueMetrics` computed once, at module load,
// over `mockCases`. The store handed it straight to the dashboard, so the KPI
// band described the mock array for ever while the queue beneath it described
// the live store. First the numbers were hardcoded and went stale when a
// sixteenth case was appended; then they were derived from the array and went
// stale again the moment real AI Employee runs were merged into the store
// ahead of the mocks. A snapshot of one list cannot describe another.
//
// The band is now computed from the cases the store is holding, by
// `computeQueueMetrics` in `utils/case.utils.ts`, which is also what the queue's
// own chips count through. The one figure that genuinely lives here is the
// authored average below: no case carries a per-case duration, so nothing in the
// data could produce it.

/**
 * Minutes, authored. The "vs 4.2 hrs industry avg" comparison on the dashboard
 * rests on this, and no per-case timing backs it. It is a stated figure, kept in
 * the mock file so it reads as one.
 */
export const AUTHORED_AVG_RESOLUTION_MINUTES = 5.2

// ── Case Drivers ───────────────────────────────────────────────────────────
export const mockCaseDrivers: CaseDriver[] = [
  {
    caseType: 'pto_leave_balance',
    label: 'PTO / Leave Balance',
    count: 47,
    avgResolutionMinutes: 3.8,
    autonomousRate: 97,
  },
  {
    caseType: 'benefits_policy_question',
    label: 'Benefits Policy Questions',
    count: 31,
    avgResolutionMinutes: 5.4,
    autonomousRate: 94,
  },
  {
    caseType: 'payroll_query',
    label: 'Payroll Discrepancies',
    count: 26,
    avgResolutionMinutes: 18.2,
    autonomousRate: 71,
  },
  {
    caseType: 'employment_verification',
    label: 'Employment Verification',
    count: 19,
    avgResolutionMinutes: 4.1,
    autonomousRate: 100,
  },
  {
    caseType: 'accommodation_disability',
    label: 'Accommodation Requests',
    count: 6,
    avgResolutionMinutes: 1440,
    autonomousRate: 0,
  },
  {
    caseType: 'general_hr_faq',
    label: 'General HR FAQ',
    count: 38,
    avgResolutionMinutes: 3.2,
    autonomousRate: 99,
  },
  {
    caseType: 'performance_data_request',
    label: 'Performance Data Requests',
    count: 8,
    avgResolutionMinutes: 45,
    autonomousRate: 25,
  },
]
